import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, 
  Search, 
  ChevronLeft, 
  ChevronRight,
  AlertTriangle,
  UserCircle,
  Shield,
  ShoppingBag,
  MoreVertical,
  Trash2,
  Plus,
  Pencil
} from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { adminApi } from '../../services/api';
import { AdminUserRecord, PaginationInfo, CreateUserResponse } from '../../types';
import { handleApiError } from '../../utils/errorHandling';
import { useSubscriptionError } from '../../context/SubscriptionErrorContext';
import Toast from '../../components/ui/Toast';
import CreateUserModal from '../../components/admin/CreateUserModal';
import EditUserModal from '../../components/admin/EditUserModal';

const ITEMS_PER_PAGE = 20;

const AllUsersPage: React.FC = () => {
  const [users, setUsers] = useState<AdminUserRecord[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [notification, setNotification] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUserRecord | null>(null);
  const { setSubscriptionError } = useSubscriptionError();

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminApi.getAllUsers({
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        userType: typeFilter || undefined,
      });
      setUsers(response.users || []);
      setPagination(response.pagination);
      setError(null);
    } catch (err) {
      handleApiError(err, setError, setSubscriptionError);
    } finally {
      setLoading(false);
    }
  }, [currentPage, typeFilter, setSubscriptionError]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [typeFilter]);

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    try {
      await adminApi.deleteUser(userId);
      setNotification({ type: 'success', message: 'User deleted successfully' });
      fetchUsers();
    } catch (err) {
      handleApiError(err, setError, setSubscriptionError);
    }
    setActionMenuOpen(null);
  };

  const handleCreateSuccess = (user: CreateUserResponse) => {
    setShowCreateModal(false);
    fetchUsers();
    setNotification({ 
      type: 'success', 
      message: `User ${user.personalInfo.firstName} ${user.personalInfo.lastName} created successfully!` 
    });
  };

  const handleEditUser = (user: AdminUserRecord) => {
    setSelectedUser(user);
    setShowEditModal(true);
    setActionMenuOpen(null);
  };

  const handleEditSuccess = (updatedUser: AdminUserRecord) => {
    setShowEditModal(false);
    setSelectedUser(null);
    fetchUsers();
    setNotification({ 
      type: 'success', 
      message: `User ${updatedUser.personalInfo?.firstName} ${updatedUser.personalInfo?.lastName} updated successfully!` 
    });
  };

  // Filter users by search term (client-side for current page)
  const filteredUsers = users.filter(user => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      user.personalInfo?.email?.toLowerCase().includes(searchLower) ||
      user.personalInfo?.firstName?.toLowerCase().includes(searchLower) ||
      user.personalInfo?.lastName?.toLowerCase().includes(searchLower) ||
      user.tenantId?.toLowerCase().includes(searchLower)
    );
  });

  const getUserTypeIcon = (type: string) => {
    switch (type) {
      case 'super_admin':
        return <Shield className="w-4 h-4 text-purple-500" />;
      case 'admin':
        return <UserCircle className="w-4 h-4 text-blue-500" />;
      case 'merchant':
        return <ShoppingBag className="w-4 h-4 text-green-500" />;
      default:
        return <UserCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getUserTypeBadge = (type: string) => {
    const styles: Record<string, string> = {
      super_admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      admin: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      merchant: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    };
    return styles[type] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold dark:text-white text-gray-900 flex items-center gap-3">
            <Users className="w-8 h-8" />
            All Users
          </h1>
          <p className="text-sm dark:text-gray-400 text-gray-600 mt-1">
            Manage all users across all tenants
          </p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Create User
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, or tenant..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-700 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          
          {/* Type Filter */}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-2 rounded-lg border dark:border-gray-600 border-gray-300 dark:bg-gray-700 bg-white dark:text-white text-gray-900 focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="">All Types</option>
            <option value="consumer">Consumer</option>
            <option value="merchant">Merchant</option>
            <option value="super_admin">Super Admin</option>
          </select>
        </div>
      </Card>

      {/* Error */}
      {error && (
        <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Users Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b dark:border-gray-700 border-gray-200">
                <th className="text-left py-3 px-4 dark:text-gray-300 text-gray-700 font-medium">User</th>
                <th className="text-left py-3 px-4 dark:text-gray-300 text-gray-700 font-medium">Tenant</th>
                <th className="text-left py-3 px-4 dark:text-gray-300 text-gray-700 font-medium">Type</th>
                <th className="text-left py-3 px-4 dark:text-gray-300 text-gray-700 font-medium">Created</th>
                <th className="text-right py-3 px-4 dark:text-gray-300 text-gray-700 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-12">
                    <Users className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p className="dark:text-gray-400 text-gray-600">
                      {searchTerm || typeFilter
                        ? 'No users match your filters'
                        : 'No users found'}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr 
                    key={user.userId}
                    className="border-b dark:border-gray-700 border-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          {getUserTypeIcon(user.type)}
                        </div>
                        <div>
                          <p className="font-medium dark:text-white text-gray-900">
                            {user.personalInfo?.firstName} {user.personalInfo?.lastName}
                          </p>
                          <p className="text-sm dark:text-gray-400 text-gray-600">
                            {user.personalInfo?.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium dark:text-gray-300 text-gray-700">
                          {user.tenantName}
                        </span>
                        <span className="text-xs font-mono dark:text-gray-500 text-gray-500">
                          {user.tenantId}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${getUserTypeBadge(user.type)}`}>
                        {getUserTypeIcon(user.type)}
                        {user.type.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="py-3 px-4 dark:text-gray-400 text-gray-600 text-sm">
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end relative">
                        <button
                          onClick={() => setActionMenuOpen(actionMenuOpen === user.userId ? null : user.userId)}
                          className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                          disabled={user.type === 'super_admin'}
                        >
                          <MoreVertical className={`w-4 h-4 ${user.type === 'super_admin' ? 'text-gray-300 dark:text-gray-600' : 'dark:text-gray-400 text-gray-600'}`} />
                        </button>
                        
                        {/* Action Dropdown */}
                        {actionMenuOpen === user.userId && user.type !== 'super_admin' && (
                          <div className="absolute right-0 top-full mt-1 w-48 rounded-lg shadow-lg dark:bg-gray-800 bg-white border dark:border-gray-700 border-gray-200 z-50">
                            <div className="py-1">
                              <button
                                onClick={() => handleEditUser(user)}
                                className="w-full flex items-center gap-2 px-4 py-2 text-sm dark:text-gray-300 text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700"
                              >
                                <Pencil className="w-4 h-4" />
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteUser(user.userId)}
                                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t dark:border-gray-700 border-gray-200">
            <p className="text-sm dark:text-gray-400 text-gray-600">
              Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, pagination.total)} of {pagination.total} users
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1 || loading}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm dark:text-gray-300 text-gray-700 px-3">
                Page {currentPage} of {pagination.pages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(pagination.pages, p + 1))}
                disabled={currentPage === pagination.pages || loading}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Click outside to close action menu */}
      {actionMenuOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setActionMenuOpen(null)}
        />
      )}

      {/* Toast Notification */}
      {notification && (
        <Toast
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <EditUserModal
          user={selectedUser}
          onClose={() => {
            setShowEditModal(false);
            setSelectedUser(null);
          }}
          onSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
};

export default AllUsersPage;
